<template>
	<div class="mb-6">
		<SharedForm
			v-if="loginFormIsVisible"
			ref="loginForm"
			:show-error="false"
			error-message=""
			:no-submit-button-gap="true"
			@submit.prevent="handleSubmitClick"
		>
			<SharedTextfield
				v-model="email"
				:pattern-error-message="$t('validation.email')"
				name="j_username"
				:display-name="$t('email-display-name')"
				type="email"
				autocomplete="username"
				autocapitalize="none"
				autocorrect="off"
				inputmode="email"
				:label="$t(store.isLogin ? 'login.email_input_placeholder' : 'registration.email_input_placeholder')"
				class="mb-4"
				:forced-error-message="store.httpErrorStatusCode === 422 ? ' ' : undefined"
				maxlength="126"
				validate-on-change
				@input="handleOnInput"
				@change="(_, data) => handleOnChange('email', data.errorMessage)"
				@form-submit="(_, data) => handleOnSubmit('email', data.errorMessage)"
			/>
			<GlobalError class="mt-2" />
			<SharedButton type="submit" class="mt-2 w-full" :is-loading="isSubmitLoading" @click="ocmButtonClick">
				{{ $t(store.isLogin ? "login.submit_button" : "registration.submit_button") }}
			</SharedButton>
		</SharedForm>

		<div v-show="!loginFormIsVisible">
			<BasicSkeleton class="mb-4 h-15"></BasicSkeleton>
			<BasicSkeleton class="mt-2 h-11.5"></BasicSkeleton>
		</div>
	</div>
</template>

<script setup lang="ts">
import { onMounted, ref, watch } from "vue";
import { pushEvent } from "@/services/dataLayerLogin";
import GlobalError from "@/components/GlobalError.vue";
import SharedButton from "@SharedVueComponents/SharedButton.vue";
import SharedForm from "@SharedVueComponents/SharedForm/SharedForm.vue";
import SharedTextfield from "@SharedVueComponents/SharedNewTextfield.vue";
import { store } from "@/state/store";
import { authenticate } from "@/services/ocmAuthentication";
import BasicSkeleton from "@SharedVueComponents/BasicSkeleton/BasicSkeleton.vue";
import { $t } from "@/main";

let section: "login" | "registration" = "login";
const email = ref("");
const isSubmitLoading = ref(false);

const loginForm = ref<InstanceType<typeof SharedForm> | null>(null);
const loginFormIsVisible = ref(false);

watch(
	() => store.isLogin,
	() => {
		section = store.isLogin ? "login" : "registration";
		resetForm();
	}
);

onMounted(() => {
	section = store.isLogin ? "login" : "registration";
	if (store.isLandingPage) loginFormIsVisible.value = true;
});

function handleOnSubmit(fieldName: string, errorMessage: string | undefined) {
	errorMessage && pushEvent(`submit_${fieldName}_error`, errorMessage);
}

function handleOnInput() {
	resetGlobalError();
}

function resetForm() {
	resetValidation();
	resetGlobalError();
}

function resetValidation() {
	if (loginForm.value) {
		loginForm.value.resetFormValidation();
	}
}

function resetGlobalError() {
	store.globalErrorMessages = [];
	store.httpErrorStatusCode = null;
}

function clearFormData() {
	resetForm();
	email.value = "";
}

function requestFirstLogin() {
	// TUB-18186 show sceleton instead of form the first time modal is opened
	// reason: prevent autofill freeze in Chrome-Browsers
	// timeout Value as defined in _popup.scss .animated
	window.setTimeout(() => {
		loginFormIsVisible.value = true;
	}, 400);
}

function handleOnChange(fieldName: string, errorMessage: string | undefined) {
	errorMessage && pushEvent(`change_${fieldName}_error`, errorMessage);
}

async function handleSubmitClick() {
	isSubmitLoading.value = true;
	await submitForm();
}

async function submitForm() {
	const authData: { j_username?: string } = {};
	if (email.value) authData["j_username"] = email.value;

	const success = await authenticate(authData, section);
	if (!success) isSubmitLoading.value = false;
}

function ocmButtonClick() {
	pushEvent("OCMButton");
}

defineExpose({
	clearFormData,
	requestFirstLogin,
});
</script>
